import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CATEGORIES, CAT_MAP } from "@/lib/categories";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { Compass, ArrowRight, Clock } from "lucide-react";

export default function StartersHubPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/starters").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const byCat = items.reduce((acc, g) => { acc[g.category] = g; return acc; }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="starters-hub-page">
      <Seo
        title="Come iniziare a collezionare: guide per principianti"
        description="Dieci guide pratiche per iniziare a collezionare: carte Pokémon, orologi, auto d'epoca, monete, francobolli, fumetti, sneakers, LEGO, arte e memorabilia sportiva. Budget, falsi, conservazione e primi passi."
        path="/come-iniziare"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Come iniziare", path: "/come-iniziare" },
        ])}
      />

      <div className="flex items-center gap-2">
        <Compass size={15} className="text-gold" />
        <span className="font-meta text-[10px] uppercase tracking-[0.24em] text-stone-500">Guide per principianti</span>
      </div>
      <h1 className="mt-4 font-editorial font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
        Come <span className="mark-hl">iniziare a collezionare</span>, ambito per ambito
      </h1>
      <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-3xl">
        Ogni mondo da collezione ha le sue regole, i suoi falsi, i suoi prezzi e i suoi punti d'ingresso. Qui
        trovi una guida dedicata per categoria: cosa comprare con il primo budget, come riconoscere un pezzo
        autentico, dove comprare in Italia e online, come conservare. Se cerchi le regole che valgono per tutti,
        parti dalla{" "}
        <Link to="/collezionismo" data-testid="starters-link-pillar"
          className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2 hover:text-gold-soft transition-colors">
          guida al collezionismo
        </Link>.
      </p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 animate-pulse">
          {[...Array(6)].map((_, i) => <div key={i} className="h-40 rounded-2xl bg-panel" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {CATEGORIES.map((c) => {
            const g = byCat[c.slug];
            const cat = CAT_MAP[c.slug];
            if (!g) {
              return (
                <div key={c.slug} data-testid={`starter-soon-${c.slug}`}
                  className={`rounded-2xl bg-panel/60 border-2 border-dashed border-line border-l-[6px] ${cat.accent} p-5`}>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} />{cat.short}
                  </span>
                  <h2 className="font-editorial font-bold text-lg text-stone-500 mt-3">
                    Come iniziare a collezionare {cat.short.toLowerCase()}
                  </h2>
                  <span className="inline-flex items-center gap-1.5 font-meta text-[11px] uppercase tracking-wide text-stone-400 mt-3">
                    <Clock size={12} /> In arrivo
                  </span>
                </div>
              );
            }
            return (
              <Link key={c.slug} to={`/come-iniziare/${c.slug}`} data-testid={`starter-card-${c.slug}`}
                className={`group rounded-2xl bg-panel border-2 border-line border-l-[6px] ${cat.accent} overflow-hidden lift flex flex-col`}>
                {g.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-ink-deep">
                    <img src={g.image_url} alt={g.title} loading="lazy" decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <span className={`inline-flex w-fit items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} />{cat.name}
                  </span>
                  <h2 className="font-editorial font-bold text-xl leading-snug text-stone-900 mt-3 group-hover:text-gold transition-colors">
                    {g.title}
                  </h2>
                  <p className="text-sm text-stone-600 mt-2 line-clamp-3 flex-1">{g.meta_description}</p>
                  <span className="inline-flex items-center gap-1 font-meta text-[11px] font-semibold uppercase tracking-wide text-gold mt-4">
                    Leggi la guida <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
